import React, { useState } from "react";
import {
    FolderKanban,
    Plus,
    Users,
    Layers,
    MoreVertical,
    Trash2,
    Edit2,
    AlertCircle,
    Loader2
} from "lucide-react";
import { createWorkspace, deleteWorkspace } from "../../api/organizations";
import { getWorkspaceColor, isLimitReached } from "../../utils/organizationHelpers";
import CreateEntityModal from "./CreateEntityModal";

const WorkspaceCard = ({ workspace, index, onSelect, onDelete, onEdit, canEdit = false }) => {
    const [showMenu, setShowMenu] = useState(false);
    const colorClass = getWorkspaceColor(index);

    return (
        <div
            className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5 hover:border-teal-500/50 dark:hover:border-teal-500/50 transition-all cursor-pointer group relative"
            onClick={() => onSelect(workspace)}
        >
            {/* Menu Button - Only for admins/owners */}
            {canEdit && (
                <div className="absolute top-3 right-3">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                        }}
                        className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>
                    {showMenu && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(false);
                                }}
                            />
                            <div className="absolute right-0 mt-1 w-40 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg shadow-xl z-20 py-1">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(workspace);
                                        setShowMenu(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm dark:text-slate-300 text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    <Edit2 className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(workspace);
                                        setShowMenu(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Workspace Icon */}
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center mb-4`}>
                <FolderKanban className="w-6 h-6 text-white" />
            </div>

            {/* Workspace Info */}
            <h3 className="dark:text-white text-slate-900 font-semibold mb-1">
                {workspace.name}
            </h3>
            {workspace.description && (
                <p className="text-sm dark:text-slate-400 text-slate-500 mb-3 line-clamp-2">
                    {workspace.description}
                </p>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 text-xs dark:text-slate-400 text-slate-500">
                <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {workspace.memberCount || 0} members
                </span>
                <span className="flex items-center gap-1">
                    <Layers className="w-3.5 h-3.5" />
                    {workspace.groupCount || 0} groups
                </span>
            </div>
        </div>
    );
};

const WorkspacesList = ({
    organization,
    workspaces,
    onSelect,
    onCreateNew,
    searchQuery,
    limits,
    onRefresh,
    canEdit = false
}) => {
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState("");

    const canCreate = canEdit && !isLimitReached(workspaces.length, limits.workspaces);

    const filteredWorkspaces = workspaces.filter((ws) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            ws.name?.toLowerCase().includes(query) ||
            ws.description?.toLowerCase().includes(query)
        );
    });

    const handleCreateWorkspace = async (data) => {
        try {
            // Transform form data to match API spec
            const payload = {
                name: data.name,
                description: data.description || "",
                settings: {}
            };
            await createWorkspace(organization.id, payload);
            setCreateModalOpen(false);
            await onRefresh?.();
        } catch (err) {
            throw err;
        }
    };

    const handleDeleteWorkspace = async () => {
        if (!workspaceToDelete) return;
        setIsDeleting(true);
        setError("");
        try {
            // Backend uses workspace_id
            const wsId = workspaceToDelete.workspace_id || workspaceToDelete.id;
            await deleteWorkspace(organization.id, wsId);
            setDeleteModalOpen(false);
            setWorkspaceToDelete(null);
            await onRefresh?.();
        } catch (err) {
            setError(err.message || "Failed to delete workspace");
        } finally {
            setIsDeleting(false);
        }
    };

    const openDeleteModal = (workspace) => {
        setWorkspaceToDelete(workspace);
        setDeleteModalOpen(true);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-bold dark:text-white text-slate-900">Workspaces</h3>
                    <p className="text-sm dark:text-slate-400 text-slate-500">
                        {workspaces.length} of {limits.workspaces} workspaces used
                    </p>
                </div>
                <button
                    onClick={() => setCreateModalOpen(true)}
                    disabled={!canCreate}
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Workspace
                </button>
            </div>

            {/* Permission Warning */}
            {!canEdit && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    Only organization <strong className="mx-1">admins</strong> and <strong className="mx-1">owners</strong> can manage workspaces.
                </div>
            )}

            {/* Limit Warning */}
            {canEdit && isLimitReached(workspaces.length, limits.workspaces) && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    Workspace limit reached. Upgrade your plan to add more workspaces.
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            )}

            {/* Workspaces Grid */}
            {filteredWorkspaces.length === 0 ? (
                <div className="text-center py-12 dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl">
                    <FolderKanban className="w-12 h-12 mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                    <p className="dark:text-slate-400 text-slate-500">
                        {searchQuery ? "No workspaces match your search" : "No workspaces yet"}
                    </p>
                    {!searchQuery && canCreate && (
                        <button
                            onClick={() => setCreateModalOpen(true)}
                            className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Create your first workspace
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredWorkspaces.map((workspace, index) => (
                        <WorkspaceCard
                            key={workspace.id}
                            workspace={workspace}
                            index={index}
                            onSelect={onSelect}
                            onDelete={openDeleteModal}
                            onEdit={(ws) => console.log("Edit workspace:", ws)}
                            canEdit={canEdit}
                        />
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {createModalOpen && (
                <CreateEntityModal
                    type="workspace"
                    onClose={() => setCreateModalOpen(false)}
                    onSubmit={handleCreateWorkspace}
                    organization={organization}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">Delete Workspace?</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2">
                                    Are you sure you want to delete <span className="font-semibold dark:text-white text-slate-900">{workspaceToDelete?.name}</span>?
                                    All groups and member assignments will be removed.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => {
                                        setDeleteModalOpen(false);
                                        setWorkspaceToDelete(null);
                                    }}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteWorkspace}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Delete"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkspacesList;
